import { Stack, Typography } from '@mui/material';
import { EligibleRegisterGroup } from '@schooly/api';
import { ExpandableSearch, Loading, OnLayout, TabsWithCounters } from '@schooly/style';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ModalContent, ModalPanel } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalEmpty } from '../../../components/uikit-components/Modal/ModalEmpty';
import { ModalEmptyMainArea } from '../../../components/uikit-components/Modal/ModalEmptyMainArea';
import { ModalRightSidebar } from '../../../components/uikit-components/Modal/ModalRightSidebar';
import { emptyRegisterGroupsFilters, filterRegisterGroups } from './RegisterFilters/utils';
import { RegisterGroupRow } from './RegisterGroupRow';
import { RegisterGroupsSidebar } from './RegisterGroupsSidebar';
import { getRegisterTabItems } from './RegisterGroupsTabs';
import { PerPeriodTabContent } from './PerPeriodTabContent';
import {
  isSessionTab,
  RegisterGroupIds,
  RegisterPeriod,
  RegisterPeriodStructure,
  RegisterScope,
  RegisterTab,
  RosterGroup,
} from './types';
import { getGroupIdsForScope, getGroupsForTab } from './utils/registerGroupIds';
import { getScopeLabel } from './utils/registerLabels';
import { sortGroupsByName } from './utils/registerRoster';

type PeriodSelection = {
  structureId?: string | null;
  periodId?: string | null;
};

const getActiveScope = (
  tab: RegisterTab,
  openPeriod: RegisterPeriod | undefined,
): RegisterScope | undefined => {
  if (isSessionTab(tab)) return { session: tab };

  return openPeriod ? { periodId: openPeriod.id } : undefined;
};

type RegisterGroupsStepProps = {
  loading: boolean;
  fetching: boolean;
  eligibleGroups: EligibleRegisterGroup[];
  activeTab?: RegisterTab;
  onSwitchTab: (tab: RegisterTab) => void;
  availableTabs: RegisterTab[];
  addedGroups: RosterGroup[];
  groupIds: RegisterGroupIds;
  initialRosterIds: RegisterGroupIds;
  isEdit: boolean;
  timetableStructures: RegisterPeriodStructure[];
  periodsByStructureId: Record<string, RegisterPeriod[]>;
  timetableStructuresLoading: boolean;
  onAddGroupId: (id: string, scope: RegisterScope) => void;
  onRemoveGroupId: (id: string, scope: RegisterScope) => void;
};

const noRegisterGroupsFilters = emptyRegisterGroupsFilters();

const AddedGroupsList: FC<{
  groups: RosterGroup[];
  scope: RegisterScope;
  loading: boolean;
  hasSearchQuery: boolean;
  onRemoveGroupId: (id: string) => void;
}> = ({ groups, scope, loading, hasSearchQuery, onRemoveGroupId }) => {
  const { formatMessage } = useIntl();

  if (loading) {
    return (
      <ModalContent sx={{ pt: 0 }} active>
        <Loading />
      </ModalContent>
    );
  }

  if (!groups.length) {
    return (
      <ModalContent sx={{ pt: 0 }} active>
        {hasSearchQuery ? (
          <ModalEmpty>
            <Typography align="center" variant="h3">
              <FormattedMessage id="attendance-NoGroupsMatchingSearch" />
            </Typography>
          </ModalEmpty>
        ) : (
          <ModalEmptyMainArea label={formatMessage({ id: 'assessments-Groups' }).toLowerCase()} />
        )}
      </ModalContent>
    );
  }

  const scopeLabel = getScopeLabel({ scope, formatMessage });

  return (
    <ModalContent active>
      <Stack>
        {groups.map((group) => (
          <RegisterGroupRow
            key={group.group_id}
            group={group}
            scope={scope}
            scopeLabel={scopeLabel}
            onRemove={() => onRemoveGroupId(group.group_id)}
          />
        ))}
      </Stack>
    </ModalContent>
  );
};

export const RegisterGroupsStep: FC<RegisterGroupsStepProps> = ({
  loading,
  fetching,
  eligibleGroups,
  activeTab,
  onSwitchTab,
  availableTabs,
  addedGroups,
  groupIds,
  initialRosterIds,
  isEdit,
  timetableStructures,
  periodsByStructureId,
  timetableStructuresLoading,
  onAddGroupId,
  onRemoveGroupId,
}) => {
  const { formatMessage } = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const [hasRef, setRef] = useState(false);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSwitchTab = useCallback(
    (_event: React.SyntheticEvent, tab: RegisterTab) => {
      setSearchQuery('');
      onSwitchTab(tab);
    },
    [onSwitchTab],
  );

  const tab = activeTab ?? availableTabs[0];

  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({});
  const defaultStructureId = timetableStructures[0]?.id ?? null;
  const defaultPeriodId = periodsByStructureId[defaultStructureId ?? '']?.[0]?.id ?? null;
  const openStructureId =
    periodSelection.structureId === undefined ? defaultStructureId : periodSelection.structureId;
  const openPeriodId =
    periodSelection.periodId === undefined ? defaultPeriodId : periodSelection.periodId;
  const openPeriod = Object.values(periodsByStructureId)
    .flat()
    .find((period) => period.id === openPeriodId);

  const handleStructureToggle = (structureId: string) => {
    if (openStructureId === structureId) {
      setPeriodSelection({ structureId: null, periodId: null });
      return;
    }

    setPeriodSelection({
      structureId,
      periodId: periodsByStructureId[structureId]?.[0]?.id ?? null,
    });
  };

  const handlePeriodToggle = (periodId: string) => {
    setPeriodSelection({
      structureId: openStructureId,
      periodId: openPeriodId === periodId ? null : periodId,
    });
  };

  const tabItems = useMemo(
    () =>
      getRegisterTabItems({
        availableTabs,
        addedGroups,
        groupIds,
        timetableStructures,
        periodsByStructureId,
        formatMessage,
      }),
    [
      addedGroups,
      availableTabs,
      formatMessage,
      groupIds,
      periodsByStructureId,
      timetableStructures,
    ],
  );

  const filteredGroups = useMemo(() => {
    const added = getGroupsForTab(addedGroups, groupIds, tab);
    return sortGroupsByName(filterRegisterGroups(added, searchQuery, noRegisterGroupsFilters));
  }, [addedGroups, groupIds, searchQuery, tab]);

  const activeScope = getActiveScope(tab, openPeriod);
  const sidebar = activeScope
    ? {
        badgeLabel: getScopeLabel({
          scope: activeScope,
          periodName: openPeriod?.name,
          formatMessage,
        }),
        scope: activeScope,
        addedGroupIds: groupIds,
        periods: Object.values(periodsByStructureId).flat(),
        initialRosterIds: getGroupIdsForScope(initialRosterIds, activeScope),
        onSelectGroupId: (id: string) => onAddGroupId(id, activeScope),
      }
    : null;

  if (loading) {
    return <Loading />;
  }

  if (!availableTabs.length) {
    return (
      <ModalEmpty>
        <Typography align="center" variant="h3" color="text.secondary">
          <FormattedMessage id="attendance-NoRegisterGroups" />
        </Typography>
      </ModalEmpty>
    );
  }

  return (
    <>
      <ModalPanel ref={panelRef} active sx={{ pt: 2.5, pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <TabsWithCounters
            tabItems={tabItems}
            onChange={handleSwitchTab}
            activeTab={tab}
            sx={{
              '& .MuiTab-root': { pb: 1, '&:not(:last-child)': { mr: 5 } },
              '& .MuiTab-root:not(.Mui-selected)': { color: 'common.grey' },
            }}
          />
          <ExpandableSearch
            value={searchQuery}
            onChange_MemoizedCallbackOnly={handleSearchChange}
            placeholder={formatMessage({ id: 'people-Search' })}
            withDebounce
            iconProps={{ sx: { color: 'common.grey' } }}
          />
        </Stack>
        <OnLayout onLayout={setRef} />
      </ModalPanel>

      {hasRef && sidebar && (
        <ModalRightSidebar open anchorEl={panelRef.current}>
          <RegisterGroupsSidebar
            badgeLabel={sidebar.badgeLabel}
            scope={sidebar.scope}
            eligibleGroups={eligibleGroups}
            addedGroupIds={sidebar.addedGroupIds}
            periods={sidebar.periods}
            initialRosterIds={sidebar.initialRosterIds}
            isEdit={isEdit}
            loading={fetching}
            onSelectGroupId={sidebar.onSelectGroupId}
          />
        </ModalRightSidebar>
      )}

      {isSessionTab(tab) ? (
        <AddedGroupsList
          groups={filteredGroups}
          scope={{ session: tab }}
          loading={fetching}
          hasSearchQuery={!!searchQuery.trim()}
          onRemoveGroupId={(id) => onRemoveGroupId(id, { session: tab })}
        />
      ) : (
        <PerPeriodTabContent
          structures={timetableStructures}
          periodsByStructureId={periodsByStructureId}
          loading={timetableStructuresLoading}
          isEdit={isEdit}
          groups={filteredGroups}
          hasSearchQuery={!!searchQuery.trim()}
          groupIds={groupIds.periods}
          openStructureId={openStructureId}
          openPeriodId={openPeriodId}
          onToggleStructure={handleStructureToggle}
          onTogglePeriod={handlePeriodToggle}
          onRemoveGroupId={(id, periodId) => onRemoveGroupId(id, { periodId })}
        />
      )}
    </>
  );
};
